import { useCallback, useState } from 'react';
import {
  SoChiTietCongNoNhieuKhachHangItem,
  SoChiTietCongNoNhieuKhachHangResponse,
  SoChiTietCongNoNhieuKhachHangSearchFormValues,
  UseSoChiTietCongNoNhieuKhachHangReturn
} from '@/types/schemas';
import api from '@/lib/api';

const generateMockData = (): SoChiTietCongNoNhieuKhachHangItem[] => {
  return [
    // Dòng tổng cộng (STT 1) - in đậm
    {
      id: 'total',
      ma_kh: '',
      ten_kh: 'Tổng cộng',
      no_dk: 3000000, // Tổng dư nợ đầu
      co_dk: 500000, // Tổng dư có đầu
      ps_no: 1500000, // Tổng ps nợ
      ps_co: 1200000, // Tổng ps có
      no_ck: 3800000, // Tổng dư nợ cuối
      co_ck: 1000000 // Tổng dư có cuối
    },
    {
      id: '1',
      ma_kh: 'KH001',
      ten_kh: 'Công ty TNHH ABC',
      no_dk: 1000000,
      co_dk: 0,
      ps_no: 500000,
      ps_co: 200000,
      no_ck: 1300000,
      co_ck: 0
    },
    {
      id: '2',
      ma_kh: 'KH002',
      ten_kh: 'Công ty CP XYZ',
      no_dk: 2000000,
      co_dk: 0,
      ps_no: 800000,
      ps_co: 300000,
      no_ck: 2500000,
      co_ck: 0
    },
    {
      id: '3',
      ma_kh: 'KH003',
      ten_kh: 'Doanh nghiệp tư nhân DEF',
      no_dk: 0,
      co_dk: 500000,
      ps_no: 200000,
      ps_co: 700000,
      no_ck: 0,
      co_ck: 1000000
    }
  ];
};
/**
 * Custom hook for managing SoChiTietCongNoNhieuKhachHang (Multiple Customer Debt Detail Report) data
 *
 * This hook provides functionality to fetch multiple customer debt detail data
 * with mock support for testing and development purposes.
 */
export function useSoChiTietCongNoNhieuKhachHang(
  searchParams: SoChiTietCongNoNhieuKhachHangSearchFormValues
): UseSoChiTietCongNoNhieuKhachHangReturn {
  const [data, setData] = useState<SoChiTietCongNoNhieuKhachHangItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (searchParams: SoChiTietCongNoNhieuKhachHangSearchFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const mockData = generateMockData();

      const response = await api.get<SoChiTietCongNoNhieuKhachHangResponse>(
        '/ban-hang/cong-no-khach-hang/so-chi-tiet-cong-no-nhieu-khach-hang/',
        {
          mock: true,
          mockData,
          params: searchParams
        }
      );

      const results = response.data?.results || response.data || mockData;
      setData(results);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred while fetching data';
      setError(new Error(errorMessage));
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await fetchData(searchParams);
  }, [fetchData, searchParams]);

  return {
    data,
    isLoading,
    error,
    fetchData,
    refreshData
  };
}
